import { irrigationSchedulerEventsCreate } from 'App/irrigation-scheduler/utils/api/irrigationSchedulerEventsCreate'
import { irrigationSchedulerEventsGet } from 'App/irrigation-scheduler/utils/api/irrigationSchedulerEventsGet'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { captureFrontendError } from 'utils/captureFrontendError'
import { showNotification } from 'utils/showNotification'
import type { TIrrigationPlanData } from '../types'

const MINUTES_PER_DAY = 1440

export const saveIrrigationSchedules = async (
  scheduleData: TIrrigationPlanData[],
  initialScheduleData: TIrrigationPlanData[],
): Promise<boolean> => {
  if (!scheduleData || scheduleData.length === 0) {
    showNotification({
      message: translate.phrases.banyanApp('No irrigation schedules to save.'),
      type: 'warning',
    })

    return false
  }

  const properties = fieldAssetStore.getState().properties
  const selectedPropertyId = selectedFieldAssetsStore.getState().property
  const selectedProperty = properties?.[Number(selectedPropertyId)]

  if (!selectedProperty?.timezone) {
    showNotification({
      message: translate.phrases.banyanApp('Failed to save irrigation schedules. Please try again.'),
      type: 'error',
    })

    return false
  }

  const { timezone } = selectedProperty
  const initialDurations: Record<string, number> = {}

  initialScheduleData.forEach((emitter) =>
    Object.values(emitter.zones || {}).forEach((zone) => {
      zone.dailyEvents.forEach((event) => {
        initialDurations[`${zone.zoneId}|||${emitter.emitterType}|||${event.date}`] = event.duration
      })
    }),
  )

  const zoneIds = new Set<number>()

  const events = scheduleData.flatMap((emitter) =>
    Object.values(emitter.zones || {}).flatMap((zone) => {
      zoneIds.add(zone.zoneId)

      return zone.dailyEvents
        .filter((event) => {
          if (event.duration <= 0) return false

          const key = `${zone.zoneId}|||${emitter.emitterType}|||${event.date}`
          const initialDuration = initialDurations[key] ?? 0

          return event.duration !== initialDuration
        })
        .map((event) => {
          const cappedDuration = Math.min(event.duration, MINUTES_PER_DAY)

          return {
            dateFrom: moment.tz(event.date, timezone).startOf('day').toISOString(),
            dateTo: moment
              .tz(event.date, timezone)
              .startOf('day')
              .add(cappedDuration, 'minutes')
              .toISOString(),
            emitterType: emitter.emitterType,
            irrigationZoneId: zone.zoneId,
            irrigationZoneEmitterId: `${zone.zoneId}|||${emitter.emitterType}`,
          }
        })
    }),
  )

  if (events.length === 0) {
    showNotification({
      message: translate.phrases.banyanApp('No irrigation schedules to save.'),
      type: 'warning',
    })

    return false
  }

  const dateFroms = events.map((e) => e.dateFrom)
  const dateTos = events.map((e) => e.dateTo)
  const dateFrom = dateFroms.reduce((min, d) => (d < min ? d : min), dateFroms[0])
  const dateTo = dateTos.reduce((max, d) => (d > max ? d : max), dateTos[0])

  try {
    const existingEvents = await irrigationSchedulerEventsGet({
      irrigationZoneIds: Array.from(zoneIds),
      dateFrom,
      dateTo,
    })

    if (Array.isArray(existingEvents)) {
      const hasOverlapping = existingEvents.some((zone) =>
        zone.scheduledIrrigationEvents.some((existing) => {
          if (existing.status === 'DELETED') return false

          return events.some(
            (newEvent) =>
              newEvent.irrigationZoneEmitterId === zone.irrigationZoneEmitterId &&
              newEvent.dateFrom < existing.dateTo &&
              newEvent.dateTo > existing.dateFrom,
          )
        }),
      )

      if (hasOverlapping) {
        showNotification({
          message: translate.phrases.banyanApp(
            'There are existing scheduled events in this date range. Please clear them before saving new schedules.',
          ),
          type: 'error',
        })

        return false
      }
    }

    const response = await irrigationSchedulerEventsCreate(events)

    if (typeof response === 'string') {
      showNotification({
        message: translate.phrases.banyanApp('Failed to save irrigation schedules. Please try again.'),
        type: 'error',
      })

      return false
    }

    showNotification({
      message: translate.phrases.banyanApp('Irrigation schedules saved successfully!'),
      type: 'success',
    })

    return true
  } catch (err) {
    captureFrontendError(err, { extra: { message: 'Failed to save irrigation schedules' } })

    showNotification({
      message: translate.phrases.banyanApp('Failed to save irrigation schedules. Please try again.'),
      type: 'error',
    })

    return false
  }
}
